// src/pages/Settings/master/ManageInvoiceTemplate.js
import React, { useState, useEffect } from "react";
import { Row, Col, Button, Input, FormGroup, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { get } from "helpers/api_helper";
import cookieHelper from "helpers/getCookieData";
import { showSuccessAlert } from "pages/utils/alertMessages";
import { openInvoicePrintWindow } from "pages/utils/printHelpers";
import {
  RiBillLine,
  RiPrinterLine,
  RiSaveLine,
  RiRefreshLine,
  RiEyeLine,
  RiToothLine,
  RiImageAddLine,
  RiDeleteBinLine,
  RiUpload2Line,
  RiCheckLine,
  RiLayoutMasonryLine,
  RiSettings4Line,
  RiPaletteLine,
} from "react-icons/ri";

const DEFAULT_INVOICE_THEMES = [
  {
    id: 5,
    type: "invoice",
    theme_id: "tax_invoice_standard",
    title: "Tax Invoice (GST Standard)",
    badge: "GST / Legal Compliant",
    accent_color: "#0284c7",
    header_title: "INVOICE",
    tagline: "Specialized Dental Care & Oral Health Center",
    gst_number: "29ABCDE1234F1Z5",
    signatory_title: "Authorized Medical Officer",
    terms: "1. Payments once made are non-refundable.\n2. Please preserve this invoice for insurance and tax claims.\n3. Follow-up appointments must be booked in advance.",
    show_bank_details: true,
    bank_name: "HDFC Bank",
    account_no: "50200012345678",
    ifsc_code: "HDFC0001234",
    branch: "Koramangala, Bangalore",
    notes: "Thank you for trusting 909 Dental OPD with your smile.",
    description: "Comprehensive tax invoice featuring GSTIN registration, procedure HSN codes, NEFT bank wire details, and seal.",
    is_active: true,
  },
  {
    id: 6,
    type: "invoice",
    theme_id: "aesthetic_minimalist",
    title: "Aesthetic Statement",
    badge: "High-End Aesthetic",
    accent_color: "#0f172a",
    header_title: "TREATMENT STATEMENT & INVOICE",
    tagline: "Center for Aesthetic Smile Designing & Orthodontics",
    gst_number: "29AABCT1234F2Z8",
    signatory_title: "Clinic Accounts Desk",
    terms: "1. Treatment fee includes clinical procedures, lab fabrications, and follow-up reviews.\n2. Digital invoices are synced to your patient portal.",
    show_bank_details: false,
    notes: "We are committed to delivering world-class dental excellence.",
    description: "Sleek dark-accented modern layout tailored for premium cosmetic & aligner dental studios.",
    is_active: true,
  },
];

const COLOR_PALETTES = [
  { name: "Sky Medical Blue", value: "#0284c7" },
  { name: "Royal Indigo", value: "#4f46e5" },
  { name: "Emerald Mint", value: "#059669" },
  { name: "Modern Purple", value: "#7c3aed" },
  { name: "Executive Dark Slate", value: "#0f172a" },
  { name: "Amber Gold", value: "#d97706" },
  { name: "Crimson Rose", value: "#e11d48" },
];

const MOCK_INVOICE_DATA = {
  id: 101,
  billing_no: "INV-DEMO-2026",
  date: new Date(),
  patient_name: "Mrs. Sunita Reddy",
  patient_mobile: "+91 98765 43210",
  patients: {
    title: "Mrs.",
    first_name: "Sunita",
    last_name: "Reddy",
    case_no: "909-P-101",
    mobile: "+91 98765 43210",
  },
  treatment_info: JSON.stringify([
    { _type: "Root Canal Treatment (RCT)", tooth: "16", cost: 4500, discount: 0, total: 4500, notes: "Rotary instrumentation & Bio-ceramic obturation" },
    { _type: "Monolithic Zirconia Crown Placement", tooth: "16", cost: 12000, discount: 1000, total: 11000, notes: "10-Year Warranty Card Included" },
    { _type: "Ultrasonic Scaling & Polishing", tooth: "Full Mouth", cost: 1500, discount: 0, total: 1500, notes: "Subgingival plaque removal" },
  ]),
  total: 17000,
  paid: 17000,
  pending: 0,
  payment_mode: "UPI / NetBanking",
};

const ManageInvoiceTemplate = () => {
  const { t } = useTranslation();
  const [themesList, setThemesList] = useState(DEFAULT_INVOICE_THEMES);
  const [config, setConfig] = useState(DEFAULT_INVOICE_THEMES[0]);
  const [zoomLevel, setZoomLevel] = useState(100);

  const clinic = (() => {
    try {
      const raw = cookieHelper.getCookie("_c");
      return raw ? JSON.parse(atob(raw)) : null;
    } catch (e) {
      return null;
    }
  })();

  const fetchActiveThemes = async () => {
    try {
      const res = await get("/dropdown/document-templates?type=invoice");
      if (res?.success && Array.isArray(res?.body) && res.body.length > 0) {
        setThemesList(res.body);
        // If no user override is in localStorage, set default active theme
        const saved = localStorage.getItem("invoice_template_config");
        if (!saved) {
          setConfig(res.body[0]);
        }
      }
    } catch (e) {
      console.warn("Could not load dynamic templates from API:", e);
    }
  };

  useEffect(() => {
    fetchActiveThemes();
    const saved = localStorage.getItem("invoice_template_config");
    if (saved) {
      try {
        setConfig(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse invoice template config:", e);
      }
    }
  }, []);

  const handleApplyPreset = (preset) => {
    const updated = {
      ...preset,
      logoImage: config.logoImage || preset.logoImage || '',
      signImage: config.signImage || preset.signImage || '',
      accentColor: preset.accent_color || preset.accentColor || "#0284c7",
      headerTitle: preset.header_title || preset.headerTitle || "INVOICE",
      gstNumber: preset.gst_number || preset.gstNumber || config.gstNumber,
      signatoryTitle: preset.signatory_title || preset.signatoryTitle || "Authorized Signatory",
      terms: preset.terms || config.terms,
    };
    setConfig(updated);
    localStorage.setItem("invoice_template_config", JSON.stringify(updated));
    showSuccessAlert(`Applied "${preset.title || preset.name}" Invoice Theme!`);
  };

  const handleImageUpload = (e, fieldType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert("Image size should be less than 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setConfig((prev) => ({ ...prev, [fieldType]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (fieldType) => {
    setConfig((prev) => ({ ...prev, [fieldType]: "" }));
  };

  const handleSave = () => {
    localStorage.setItem("invoice_template_config", JSON.stringify(config));
    showSuccessAlert(t("Invoice template settings saved successfully!"));
  };

  const handleReset = () => {
    const defaultTheme = themesList[0] || DEFAULT_INVOICE_THEMES[0];
    setConfig(defaultTheme);
    localStorage.setItem("invoice_template_config", JSON.stringify(defaultTheme));
    showSuccessAlert(t("Template restored to SuperAdmin active default."));
  };

  const handleTestPrint = () => {
    openInvoicePrintWindow(MOCK_INVOICE_DATA, clinic);
  };

  const accentColor = config.accent_color || config.accentColor || "#0284c7";

  return (
    <div className="template-studio-wrapper py-1">
      {/* Top Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h4 className="font-weight-bold text-slate-800 mb-1 d-flex align-items-center gap-2">
            <RiBillLine className="text-primary" />
            {t("Clinical Tax Invoice Template Settings")}
          </h4>
          <p className="text-muted font-size-12 mb-0">
            {t("Select your preferred invoice layout from SuperAdmin active templates, and customize your clinic branding, GSTIN, and stamp.")}
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button color="light" size="sm" className="border d-flex align-items-center gap-1 font-size-12" onClick={handleReset}>
            <RiRefreshLine /> {t("Reset SuperAdmin Defaults")}
          </Button>
          <Button color="primary" size="sm" className="d-flex align-items-center gap-1 shadow-sm font-size-12 font-weight-semibold" onClick={handleTestPrint}>
            <RiPrinterLine /> {t("Test Print Invoice")}
          </Button>
        </div>
      </div>

      {/* Preset Themes Gallery: Only Active Templates Approved by SuperAdmin */}
      <div className="mb-4">
        <h6 className="font-size-13 font-weight-bold text-dark mb-2.5 d-flex align-items-center gap-1.5">
          <RiLayoutMasonryLine className="text-primary font-size-15" />
          {t("Available Active Invoice Themes (SuperAdmin Approved)")}
        </h6>
        <div className="theme-grid">
          {themesList.map((theme) => {
            const isSelected = config.theme_id === theme.theme_id || config.id === theme.id;
            const themeColor = theme.accent_color || theme.accentColor || "#0284c7";
            return (
              <div
                key={theme.id || theme.theme_id}
                className={`theme-card-box ${isSelected ? "active-theme" : ""}`}
                onClick={() => handleApplyPreset(theme)}
              >
                <div className="theme-top-row">
                  <span className="theme-color-indicator" style={{ background: themeColor }} />
                  <span
                    className="badge font-size-10"
                    style={{
                      background: isSelected ? `${themeColor}18` : "#f1f5f9",
                      color: isSelected ? themeColor : "#475569",
                      border: `1px solid ${isSelected ? `${themeColor}40` : "#e2e8f0"}`,
                    }}
                  >
                    {theme.badge || "GST Standard"}
                  </span>
                </div>
                <div className="theme-card-title">{theme.title || theme.name}</div>
                <div className="theme-card-desc">{theme.description}</div>
                <div className="theme-card-footer">
                  <span className="text-muted font-size-11 font-weight-semibold">
                    {isSelected ? "✓ Clinic Active Choice" : "Click to Select"}
                  </span>
                  <span className="badge bg-light text-dark border font-size-10">A4 Document</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Split Workspace */}
      <Row className="g-4">
        <Col lg={5} md={12}>
          <div className="customizer-card">
            <div className="section-title">
              <RiSettings4Line className="text-primary font-size-16" />
              <span>{t("Clinic Invoice Customization")}</span>
            </div>

            {/* Logo & Signature Uploader */}
            <div className="p-3 mb-4 rounded border bg-light">
              <div className="font-size-12 font-weight-bold text-dark mb-2.5 d-flex align-items-center gap-1.5">
                <RiImageAddLine className="text-primary font-size-15" />
                {t("Clinic Logo & Doctor Signature")}
              </div>
              <Row className="g-3">
                <Col sm={6}>
                  <Label className="font-size-11 font-weight-semibold text-secondary mb-1">
                    {t("Clinic Logo")}
                  </Label>
                  {config.logoImage ? (
                    <div className="position-relative p-2 bg-white border rounded text-center">
                      <img
                        src={config.logoImage}
                        alt="Logo Preview"
                        style={{ maxHeight: "40px", maxWidth: "100%", objectFit: "contain" }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="btn-icon position-absolute"
                        style={{ top: "-8px", right: "-8px", width: "22px", height: "22px", padding: 0, borderRadius: "50%" }}
                        onClick={() => handleRemoveImage("logoImage")}
                      >
                        <RiDeleteBinLine className="font-size-11" />
                      </Button>
                    </div>
                  ) : (
                    <label className="btn btn-outline-secondary btn-sm font-size-11 mb-0 w-100 d-flex align-items-center justify-content-center gap-1 cursor-pointer">
                      <RiUpload2Line /> {t("Upload Logo")}
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleImageUpload(e, "logoImage")} />
                    </label>
                  )}
                  <small className="text-muted font-size-10 d-block mt-1">PNG/JPG transparent</small>
                </Col>

                <Col sm={6}>
                  <Label className="font-size-11 font-weight-semibold text-secondary mb-1">
                    {t("Signature / Stamp")}
                  </Label>
                  {config.signImage ? (
                    <div className="position-relative p-2 bg-white border rounded text-center">
                      <img
                        src={config.signImage}
                        alt="Signature Preview"
                        style={{ maxHeight: "40px", maxWidth: "100%", objectFit: "contain" }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="btn-icon position-absolute"
                        style={{ top: "-8px", right: "-8px", width: "22px", height: "22px", padding: 0, borderRadius: "50%" }}
                        onClick={() => handleRemoveImage("signImage")}
                      >
                        <RiDeleteBinLine className="font-size-11" />
                      </Button>
                    </div>
                  ) : (
                    <label className="btn btn-outline-secondary btn-sm font-size-11 mb-0 w-100 d-flex align-items-center justify-content-center gap-1 cursor-pointer">
                      <RiUpload2Line /> {t("Upload Signature")}
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleImageUpload(e, "signImage")} />
                    </label>
                  )}
                  <small className="text-muted font-size-10 d-block mt-1">Doctor sign or round seal</small>
                </Col>
              </Row>
            </div>

            <FormGroup>
              <Label>{t("Invoice Header Title")}</Label>
              <Input
                type="text"
                value={config.header_title || config.headerTitle || ""}
                onChange={(e) => setConfig({ ...config, header_title: e.target.value, headerTitle: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>{t("GSTIN / Clinic Tax Registration ID")}</Label>
              <Input
                type="text"
                value={config.gst_number || config.gstNumber || ""}
                onChange={(e) => setConfig({ ...config, gst_number: e.target.value, gstNumber: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>{t("Theme Accent Color")}</Label>
              <div className="d-flex align-items-center gap-2.5 flex-wrap mt-1">
                {COLOR_PALETTES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className="btn p-0 rounded-circle border shadow-2xs position-relative transition-all"
                    style={{
                      width: "28px",
                      height: "28px",
                      background: c.value,
                      transform: (accentColor === c.value) ? "scale(1.2)" : "scale(1)",
                      border: (accentColor === c.value) ? "2px solid #0f172a" : "1px solid #cbd5e1",
                    }}
                    onClick={() => setConfig({ ...config, accent_color: c.value, accentColor: c.value })}
                    title={c.name}
                  >
                    {(accentColor === c.value) && (
                      <RiCheckLine className="text-white position-absolute" style={{ top: "4px", left: "4px" }} />
                    )}
                  </button>
                ))}
                <div
                  className="position-relative rounded-circle overflow-hidden border shadow-2xs d-flex align-items-center justify-content-center ms-1"
                  style={{
                    width: "28px",
                    height: "28px",
                    minWidth: "28px",
                    minHeight: "28px",
                    background: accentColor,
                    border: "1px solid #cbd5e1",
                    cursor: "pointer",
                  }}
                  title="Custom Color"
                >
                  <input
                    type="color"
                    className="position-absolute"
                    style={{
                      opacity: 0,
                      width: "100%",
                      height: "100%",
                      cursor: "pointer",
                      top: 0,
                      left: 0,
                    }}
                    value={accentColor}
                    onChange={(e) => setConfig({ ...config, accent_color: e.target.value, accentColor: e.target.value })}
                  />
                  <RiPaletteLine className="text-white font-size-14 pointer-events-none" style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.6))" }} />
                </div>
              </div>
            </FormGroup>

            <FormGroup>
              <Label>{t("Terms & Conditions")}</Label>
              <Input
                type="textarea"
                rows={3}
                value={config.terms || ""}
                onChange={(e) => setConfig({ ...config, terms: e.target.value })}
              />
            </FormGroup>

            <div className="d-flex gap-2 mt-4">
              <Button color="primary" size="sm" className="flex-grow-1 font-size-12 font-weight-semibold shadow-sm py-2" onClick={handleSave}>
                <RiSaveLine className="me-1" /> {t("Save Clinic Invoice Settings")}
              </Button>
              <Button color="light" size="sm" className="border px-3 py-2 font-size-12" onClick={handleTestPrint}>
                <RiPrinterLine className="me-1" /> {t("Test Print")}
              </Button>
            </div>
          </div>
        </Col>

        {/* Live A4 Canvas Simulator */}
        <Col lg={7} md={12}>
          <div className="bg-white border rounded-3 p-3 shadow-2xs">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 bg-light p-2.5 rounded border">
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-dark text-white font-size-11 px-2.5 py-1">
                  <RiEyeLine className="me-1" /> {t("Live Document Preview")}
                </span>
                <span className="badge bg-white text-muted border font-size-11">A4 Aspect</span>
              </div>
              <div className="btn-group btn-group-sm">
                <Button color={zoomLevel === 85 ? "primary" : "light"} className="font-size-11 py-1 px-2.5" onClick={() => setZoomLevel(85)}>
                  85%
                </Button>
                <Button color={zoomLevel === 100 ? "primary" : "light"} className="font-size-11 py-1 px-2.5" onClick={() => setZoomLevel(100)}>
                  100%
                </Button>
              </div>
            </div>

            <div className="paper-canvas-outer">
              <div
                className="paper-sheet"
                style={{
                  borderTop: `5px solid ${accentColor}`,
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: "top center",
                }}
              >
                <div className="paper-watermark">PAID</div>
                <div className="d-flex flex-column justify-content-between h-100 position-relative" style={{ minHeight: "740px" }}>
                  <div>
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
                      <div>
                        {config.logoImage ? (
                          <img
                            src={config.logoImage}
                            alt="Clinic Logo"
                            className="mb-2"
                            style={{ maxHeight: "48px", maxWidth: "160px", objectFit: "contain" }}
                          />
                        ) : (
                          <div className="d-inline-flex align-items-center gap-1 px-2 py-0.5 mb-1.5 rounded border border-dashed text-muted font-size-10 bg-light">
                            <RiImageAddLine className="font-size-12 text-primary" />
                            <span>Clinic Logo Space</span>
                          </div>
                        )}
                        <div className="d-flex align-items-center gap-2">
                          <h4 className="font-size-17 font-weight-bold mb-0" style={{ color: accentColor }}>
                            {clinic?.clinic_name || "909 Dental OPD & Implant Center"}
                          </h4>
                        </div>
                        <div className="font-size-11 font-weight-semibold text-secondary mt-0.5">{config.tagline}</div>
                        <div className="font-size-10 text-muted mt-1">
                          GSTIN: <strong>{config.gst_number || config.gstNumber}</strong> • Phone: {clinic?.phone || "098220 11111"}
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="badge bg-light text-dark border font-size-11 px-2.5 py-1 mb-1">
                          {config.header_title || config.headerTitle}
                        </div>
                        <div className="font-size-13 font-weight-bold text-dark">#INV-DEMO-2026</div>
                        <div className="font-size-10 text-muted">Date: {new Date().toLocaleDateString("en-GB")}</div>
                      </div>
                    </div>

                    {/* Meta Grid */}
                    <div className="bg-light p-2 rounded border mb-3 font-size-11 d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <span className="text-muted">Billed Patient: </span>
                        <strong className="text-dark">Mrs. Sunita Reddy</strong> (Case #909-P-101)
                      </div>
                      <div>
                        <span className="text-muted">Status: </span>
                        <span className="badge bg-soft-success text-success font-weight-bold">PAID (UPI)</span>
                      </div>
                    </div>

                    {/* Procedures Table */}
                    <div className="table-responsive rounded border bg-light mb-3">
                      <table className="table table-sm table-borderless mb-0 font-size-11">
                        <thead>
                          <tr className="bg-white border-bottom text-muted font-weight-bold">
                            <th className="py-1 px-2">#</th>
                            <th className="py-1 px-2">Procedure / Dental Service</th>
                            <th className="py-1 px-2 text-end">Fee</th>
                            <th className="py-1 px-2 text-end">Discount</th>
                            <th className="py-1 px-2 text-end">Net Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-bottom border-light">
                            <td className="py-1.5 px-2 text-muted">1</td>
                            <td className="py-1.5 px-2">
                              <strong className="text-dark">Root Canal Treatment (RCT) (#16)</strong>
                            </td>
                            <td className="py-1.5 px-2 text-end">₹4,500.00</td>
                            <td className="py-1.5 px-2 text-end text-muted">₹0.00</td>
                            <td className="py-1.5 px-2 text-end font-weight-bold">₹4,500.00</td>
                          </tr>
                          <tr className="border-bottom border-light">
                            <td className="py-1.5 px-2 text-muted">2</td>
                            <td className="py-1.5 px-2">
                              <strong className="text-dark">Monolithic Zirconia Crown (#16)</strong>
                            </td>
                            <td className="py-1.5 px-2 text-end">₹12,000.00</td>
                            <td className="py-1.5 px-2 text-end text-danger">-₹1,000.00</td>
                            <td className="py-1.5 px-2 text-end font-weight-bold">₹11,000.00</td>
                          </tr>
                          <tr className="border-bottom border-light">
                            <td className="py-1.5 px-2 text-muted">3</td>
                            <td className="py-1.5 px-2">
                              <strong className="text-dark">Ultrasonic Scaling & Polishing</strong>
                            </td>
                            <td className="py-1.5 px-2 text-end">₹1,500.00</td>
                            <td className="py-1.5 px-2 text-end text-muted">₹0.00</td>
                            <td className="py-1.5 px-2 text-end font-weight-bold">₹1,500.00</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Summary & Bank Grid */}
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                      <div className="p-2 rounded border bg-light font-size-10 text-muted flex-grow-1">
                        <strong className="text-dark d-block mb-1">🏦 Bank Wire Details:</strong>
                        {config.bank_name || config.bankName || "HDFC Bank"} • A/C: <strong>{config.account_no || config.accountNo || "50200012345678"}</strong><br />
                        IFSC: <strong>{config.ifsc_code || config.ifscCode || "HDFC0001234"}</strong> ({config.branch || "Koramangala"})
                      </div>
                      <div className="p-2.5 rounded border bg-light font-size-11" style={{ width: "200px" }}>
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">Total Paid:</span>
                          <span className="font-weight-bold text-dark" style={{ color: accentColor }}>
                            ₹17,000.00
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Terms & Signature */}
                  <div className="border-top pt-3 d-flex justify-content-between align-items-end font-size-11">
                    <div className="text-muted" style={{ maxWidth: "340px", whiteSpace: "pre-line" }}>
                      <strong className="text-dark d-block mb-1">Terms & Conditions:</strong>
                      {config.terms}
                    </div>
                    <div className="text-center d-flex flex-column align-items-center">
                      {config.signImage ? (
                        <img
                          src={config.signImage}
                          alt="Authorized Signatory"
                          style={{ maxHeight: "75px", maxWidth: "180px", height: "68px", objectFit: "contain", marginBottom: "4px" }}
                        />
                      ) : (
                        <div className="d-inline-flex flex-column align-items-center justify-content-center p-1.5 mb-1 rounded border border-dashed text-muted font-size-10 bg-light" style={{ width: "160px", height: "52px", borderColor: "#cbd5e1" }}>
                          <span className="font-weight-semibold" style={{ fontSize: "10px" }}>Clinic Stamp & Sign</span>
                          <span style={{ fontSize: "8.5px", opacity: 0.75 }}>Upload via Settings</span>
                        </div>
                      )}
                      <div className="border-top pt-1 font-weight-bold text-dark" style={{ width: "160px", marginTop: config.signImage ? "0" : "4px" }}>
                        {config.signatory_title || config.signatoryTitle || "Accounts Desk"}
                      </div>
                      <div className="font-size-10 text-muted">Authorized Signatory</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ManageInvoiceTemplate;
